import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import { ExternalMenuCategory } from '@/types/FoodDelivery.interface';
import FoodDeliveryEmptyState from '../../food-delivery/components/FoodDeliveryEmptyState';
import { FOOD_DELIVERY_ROUTES } from '../../food-delivery/helpers';

interface Props {
  integrationId: number;
  onMessage: (msg: string, severity: 'success' | 'error' | 'info') => void;
}

/**
 * Kategori satışa açma/kapama (plan Faz 5).
 *
 * Bir kategoriyi kapatmak, altındaki tüm ürünleri tek tek kapatmaya göre hem
 * daha hızlı hem de operatör için daha anlaşılırdır ("Tatlılar bugün yok").
 * Kategoriler menü içe aktarımıyla dolar.
 */
const CategoryAvailabilityCard: React.FC<Props> = ({
  integrationId,
  onMessage
}) => {
  const [rows, setRows] = useState<ExternalMenuCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await foodDeliveryService.listCategories(integrationId));
    } catch {
      onMessage('Kategoriler getirilemedi.', 'error');
    } finally {
      setLoading(false);
    }
    // onMessage her render'da yeniden üretiliyorsa döngü olmasın diye bağımlılık dışı.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (row: ExternalMenuCategory) => {
    setBusyId(row.id);
    const next = !row.isAvailable;
    try {
      await foodDeliveryService.setCategoryAvailability(row.id, next);
      onMessage(
        `“${row.name ?? row.externalCategoryId}” ${
          next ? 'satışa açıldı' : 'satışa kapatıldı'
        }.`,
        'success'
      );
      await load();
    } catch (err) {
      onMessage(
        `Kategori güncellenemedi: ${
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? 'bilinmeyen hata'
        }`,
        'error'
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h4" gutterBottom>
          Menü Kategorileri ({rows.length})
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Bir kategoriyi kapatmak, altındaki tüm ürünleri platformda satışa
          kapatır. Ürünleri tek tek kapatmaya göre daha hızlıdır.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <FoodDeliveryEmptyState
            message="Kategori kaydı yok. Kategoriler platform menüsü içe aktarıldığında oluşur."
            to={FOOD_DELIVERY_ROUTES.mappings}
            actionLabel="Menü Eşleştirme’ye git"
          />
        ) : (
          <Stack spacing={1}>
            {rows.map((row) => (
              <Stack
                key={row.id}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Switch
                  checked={row.isAvailable}
                  disabled={busyId === row.id}
                  onChange={() => toggle(row)}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">
                    {row.name ?? `#${row.externalCategoryId}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Platform kimliği: {row.externalCategoryId}
                    {row.lastSyncedAt &&
                      ` · son değişiklik ${new Date(
                        row.lastSyncedAt
                      ).toLocaleString('tr-TR')}`}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={row.isAvailable ? 'success' : 'default'}
                  label={row.isAvailable ? 'Satışta' : 'Kapalı'}
                />
              </Stack>
            ))}
          </Stack>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          Kategori durumu yalnız platform onayladıktan sonra burada değişir;
          böylece panel, gerçekte açık olan bir kategoriyi kapalı göstermez.
        </Alert>
      </CardContent>
    </Card>
  );
};

export default CategoryAvailabilityCard;
